import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import DownloadIcon from '@mui/icons-material/Download'
import { Box, Button, Card, CardContent, CircularProgress, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store'
import { apiSlice } from '../store/api/apiSlice'
import { API_BASE_URL } from '../config/api'
import dayjs from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import saleContractApi, {
  useGetSaleContractPagePreferencesQuery,
  useSetRepInfoMutation,
} from '../store/api/saleContractApi'
import { usePreference } from '../hooks/usePreference'

/**
 * Page-level filter shape — kept in sync with backend `SaleContractPageFilter`
 * (app/services/preferences/baselines/sale_contract_page_filter.py). The default
 * row (username='default', page_path='sales', preference_key='list_filter') is
 * seeded by SQL migration 0015, so users see populated values immediately.
 */
interface SaleContractPageFilter {
  rep_info: number | null
  shipmentPeriodFrom: string | null
  shipmentPeriodTo: string | null
  finalizedFilter: 'all' | 'yes' | 'no'
}
import consignmentApi from '../store/api/consignmentApi'
import saleAmendmentsApi from '../store/api/saleAmendmentsApi'
import salePTBFFixationsApi from '../store/api/salePTBFFixationsApi'
import PageHeader from '../components/PageHeader'
import ReloadButton from '../components/ReloadButton'
import {
  CARD_CONTENT_COMPACT,
  FILTER_HEADER_INPUT_SX,
  FILTER_HEADER_SELECT_SX,
  HIDE_ON_TABLET_INLINE,
} from '../theme/responsive'
import ChooseRepInfoDialog from './saleContract/ChooseRepInfoDialog'
import ConsignmentListView from './saleContract/ConsignmentListView'
import ContractListView from './saleContract/ContractListView'
import SaleAmendmentListView from './saleContract/SaleAmendmentListView'
import SalePTBFFixationListView from './saleContract/SalePTBFFixationListView'
import SaleReferencesView from './saleContract/SaleReferencesView'

const TAB_CONFIG = [
  { path: 'list', label: 'Sales Contract' },
  { path: 'consignment', label: 'Consignment' },
  { path: 'amendment', label: 'Amendment' },
  { path: 'ptbf-fixation', label: 'PTBF Fixation' },
  { path: 'references', label: 'References' },
] as const

type FinalizedFilter = 'all' | 'yes' | 'no'

const headerButtonSx = {
  minHeight: 24,
  px: 0.75,
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const headerLabelSx = {
  whiteSpace: 'nowrap',
  fontSize: '0.72rem',
  lineHeight: 1.1,
}

// Filter chung header sx — đã codify trong `@/theme/responsive` (Pattern 11).
// Local aliases để giữ name compat cho call site hiện có.
const compactInputSx = FILTER_HEADER_INPUT_SX
const compactSelectSx = FILTER_HEADER_SELECT_SX

const getFiscalYearDefaultRange = () => {
  const now = dayjs()
  const startYear = now.month() >= 9 ? now.year() : now.year() - 1

  return {
    startDate: `${startYear}-10-01`,
    endDate: `${startYear + 1}-09-30`,
  }
}

const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null

  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

export default function SaleContractPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [repInfoOpen, setRepInfoOpen] = useState(false)
  const [shipmentStart, setShipmentStart] = useState('')
  const [shipmentEnd, setShipmentEnd] = useState('')
  const [finalizedFilter, setFinalizedFilter] = useState<FinalizedFilter>('all')
  const [exporting, setExporting] = useState<'all' | 'filter' | null>(null)
  // Gate render tab con: chỉ mount sau khi filter prefs hydrate xong (và
  // default PATCH nếu cần) → tránh tab gọi list API 2 lần (lần đầu với filter
  // cũ/stale + lần 2 với filter đúng).
  const [filtersReady, setFiltersReady] = useState(false)
  const prefsHydrated = useRef(false)
  const lastPatchedRef = useRef<{ shipmentPeriodFrom: string; shipmentPeriodTo: string; finalizedFilter: FinalizedFilter } | null>(null)
  // pagePrefs vẫn dùng useGetSaleContractPagePreferencesQuery để lấy currentSignId
  // (rep_info nằm chung row, nhưng API legacy còn return thêm metadata reps).
  const { data: pagePrefs } = useGetSaleContractPagePreferencesQuery()
  const pageFilterPref = usePreference<SaleContractPageFilter>('sale_contract', 'page_filter')
  const dispatch = useAppDispatch()
  const [setRepInfo] = useSetRepInfoMutation()
  const email = useAppSelector((s) => s.auth.user?.email ?? '')
  const exportToken = 'JDHK-NAEB-DQCK-DGFF'

  const downloadSaleContractExcel = async (mode: 'all' | 'filter', url: string) => {
    if (!email) return

    setExporting(mode)
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      const downloadName =
        mode === 'filter' && filename
          ? filename.replace(/\.xlsx$/i, '_filter.xlsx')
          : filename
      a.href = href
      a.download = downloadName ?? (mode === 'filter' ? 'SaleContract_filter.xlsx' : 'SaleContract.xlsx')
      a.click()
      URL.revokeObjectURL(href)
    } finally {
      setExporting(null)
    }
  }

  const handleExportAll = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/salecontract/data?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}`
    await downloadSaleContractExcel('all', url)
  }

  const handleExportFilter = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/salecontract/data_filter?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}&start_date=${encodeURIComponent(shipmentStart)}&end_date=${encodeURIComponent(shipmentEnd)}&is_finalized=${encodeURIComponent(finalizedFilter)}`
    await downloadSaleContractExcel('filter', url)
  }

  useEffect(() => {
    if (pageFilterPref.isLoading || prefsHydrated.current) return
    prefsHydrated.current = true

    const { value } = pageFilterPref
    const hydratedFrom = value.shipmentPeriodFrom ?? ''
    const hydratedTo = value.shipmentPeriodTo ?? ''
    const hydratedFinalized: FinalizedFilter = (value.finalizedFilter ?? 'all') as FinalizedFilter

    lastPatchedRef.current = {
      shipmentPeriodFrom: hydratedFrom,
      shipmentPeriodTo: hydratedTo,
      finalizedFilter: hydratedFinalized,
    }

    setShipmentStart(hydratedFrom)
    setShipmentEnd(hydratedTo)
    setFinalizedFilter(hydratedFinalized)
    setFiltersReady(true)

    if (pagePrefs?.currentSignId == null) {
      void setRepInfo(1)
    }
  }, [pageFilterPref, pagePrefs?.currentSignId, setRepInfo])

  useEffect(() => {
    if (!prefsHydrated.current) return

    const last = lastPatchedRef.current
    if (last) {
      const unchanged =
        last.shipmentPeriodFrom === shipmentStart && last.shipmentPeriodTo === shipmentEnd && last.finalizedFilter === finalizedFilter
      if (unchanged) return
    }

    const timer = window.setTimeout(() => {
      lastPatchedRef.current = { shipmentPeriodFrom: shipmentStart, shipmentPeriodTo: shipmentEnd, finalizedFilter }
      ;(async () => {
        try {
          await pageFilterPref.patch({
            shipmentPeriodFrom: shipmentStart || null,
            shipmentPeriodTo: shipmentEnd || null,
            finalizedFilter,
          })
        } catch {
          // ignore PATCH failure — UX vẫn cho phép tiếp tục
        }
        // BE đọc filter từ user_preferences ⇒ invalidate list tags để các tab
        // refetch lại với filter mới. Sale Contract queries nằm trong `apiSlice`
        // (reducerPath 'api'), KHÔNG phải `baseApi` (reducerPath 'tlcerpApi') —
        // parity PurchaseContractPage flow nhưng đúng slice của domain.
        dispatch(
          apiSlice.util.invalidateTags([
            'SaleContract',
            'SaleAmendment',
            'SalePTBFFixation',
          ]),
        )
      })()
    }, 450)
    return () => window.clearTimeout(timer)
  }, [shipmentStart, shipmentEnd, finalizedFilter, pageFilterPref, dispatch])

  const activeIndex = useMemo(() => {
    const segment = location.pathname.split('/').pop() ?? ''
    const idx = TAB_CONFIG.findIndex((t) => t.path === segment)
    return idx >= 0 ? idx : 0
  }, [location.pathname])

  const current = TAB_CONFIG[activeIndex]
  const hasInvalidShipmentRange =
    Boolean(shipmentStart) && Boolean(shipmentEnd) && shipmentStart > '' && shipmentEnd > '' && shipmentStart > shipmentEnd

  // Đọc cache state của query tương ứng với tab đang active. Args phải khớp với
  // args mà các view con sử dụng (page 1, all rows).
  const listSelectArgs = useMemo(() => ({ page: 1, pageSize: 1_000_000 }), [])
  const listSelect = useMemo(() => saleContractApi.endpoints.getSaleContracts.select(listSelectArgs), [listSelectArgs])
  const consignmentSelect = useMemo(
    () => consignmentApi.endpoints.getConsignments.select(listSelectArgs),
    [listSelectArgs],
  )
  const amendmentSelect = useMemo(
    () => saleAmendmentsApi.endpoints.getSaleAmendments.select(listSelectArgs),
    [listSelectArgs],
  )
  const ptbfSelect = useMemo(
    () => salePTBFFixationsApi.endpoints.getSalePTBFFixations.select(listSelectArgs),
    [listSelectArgs],
  )
  const reloadBusy = useAppSelector((s) => {
    switch (current?.path) {
      case 'list':
        return listSelect(s).status === 'pending'
      case 'consignment':
        return consignmentSelect(s).status === 'pending'
      case 'amendment':
        return amendmentSelect(s).status === 'pending'
      case 'ptbf-fixation':
        return ptbfSelect(s).status === 'pending'
      default:
        return false
    }
  })

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Sales Contract"
        module="Sales"
        actions={
        <Stack
          direction="row"
          spacing={0.45}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
          sx={{ width: '100%' }}
        >
          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {/* Date range filter: DatePicker đầu có label "Shipment Period",
                  DatePicker thứ 2 KHÔNG label — phân biệt bằng `→` icon ở giữa.
                  Convention 2026-05-28 (chị Đẹp feedback). */}
              <DatePicker
                label="Shipment Period"
                format="DD-MMM-YYYY"
                value={shipmentStart ? dayjs(shipmentStart) : null}
                onChange={(d) => setShipmentStart(d ? d.format('YYYY-MM-DD') : getFiscalYearDefaultRange().startDate)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: compactInputSx,
                    InputLabelProps: { shrink: true },
                    inputProps: { 'aria-label': 'Shipment Period start date' },
                  },
                }}
              />
              <ArrowRightAltIcon
                fontSize="small"
                sx={{ color: 'text.secondary', fontSize: { xs: 16, sm: 18 }, mx: 0.25 }}
                aria-hidden="true"
              />
              <DatePicker
                format="DD-MMM-YYYY"
                value={shipmentEnd ? dayjs(shipmentEnd) : null}
                onChange={(d) => setShipmentEnd(d ? d.format('YYYY-MM-DD') : getFiscalYearDefaultRange().endDate)}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: hasInvalidShipmentRange,
                    sx: compactInputSx,
                    inputProps: { 'aria-label': 'Shipment Period end date' },
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>

          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <TextField
              label="Is Finalized"
              select
              size="small"
              value={finalizedFilter}
              onChange={(event) => setFinalizedFilter(event.target.value as FinalizedFilter)}
              InputLabelProps={{ shrink: true }}
              sx={compactSelectSx}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Stack>

          <Button size="small" variant="outlined" sx={headerButtonSx} onClick={() => setRepInfoOpen(true)} title="Choose Rep Info">
            {/* sm: "Rep Info" — md+: "Choose Rep Info" */}
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Choose{' '}</Box>
            Rep Info
          </Button>
          <ReloadButton
            sx={headerButtonSx}
            apiTags={['SaleContract', 'SaleContractPrefs', 'SaleContractColumnPrefs', 'SaleAmendment', 'SalePTBFFixation']}
            baseTags={[]}
            busy={reloadBusy}
          />
          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            onClick={handleExportFilter}
            disabled={exporting !== null || !email || hasInvalidShipmentRange}
            startIcon={
              exporting === 'filter' ? (
                <CircularProgress size={11} color="inherit" />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            title="Export (Filter)"
          >
            {/* xs+sm: "Filter" only (discriminator) — md+: "Export (Filter)" */}
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export ({' '}</Box>
            Filter
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>)</Box>
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            onClick={handleExportAll}
            disabled={exporting !== null || !email}
            startIcon={
              exporting === 'all' ? (
                <CircularProgress size={11} color="inherit" />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            title="Export (All)"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export ({' '}</Box>
            All
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>)</Box>
          </Button>
        </Stack>
        }
      />
      {hasInvalidShipmentRange && (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      )}
      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeIndex}
          onChange={(_, value: number) => {
            navigate(`/sales/contract/${TAB_CONFIG[value].path}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 34,
            '& .MuiTabs-indicator': {
              height: 2.5,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              position: 'relative',
              minHeight: 34,
              minWidth: 0,
              py: 0.25,
              px: 1.5,
              fontSize: '0.78rem',
              fontWeight: 600,
              letterSpacing: 0.15,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: '6px 6px 0 0',
              transition: 'color 140ms ease, background-color 140ms ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
              // vạch ngăn dọc ngắn, căn giữa, mảnh giữa các tab — ẩn cạnh tab đang chọn/hover cho sạch
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: '1px',
                bgcolor: 'divider',
                opacity: 0.6,
                transition: 'opacity 140ms ease',
              },
              '&:hover::after, &.Mui-selected::after': { opacity: 0 },
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.path} label={tab.label} />
          ))}
        </Tabs>
        <CardContent sx={CARD_CONTENT_COMPACT}>
          {!filtersReady ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={20} />
            </Box>
          ) : current.path === 'list' ? (
            <ContractListView
              shipmentPeriodStart={shipmentStart}
              shipmentPeriodEnd={shipmentEnd}
              finalizedFilter={finalizedFilter}
            />
          ) : current.path === 'consignment' ? (
            <ConsignmentListView />
          ) : current.path === 'amendment' ? (
            <SaleAmendmentListView />
          ) : current.path === 'ptbf-fixation' ? (
            <SalePTBFFixationListView />
          ) : current.path === 'references' ? (
            <SaleReferencesView />
          ) : (
            <Box sx={{ py: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {TAB_CONFIG[activeIndex]?.label ?? 'Sales Contract'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This screen has not been migrated from Access yet. Use <strong>Sales Contract</strong> for list / New /
                Edit / Print workflows.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      <ChooseRepInfoDialog open={repInfoOpen} onClose={() => setRepInfoOpen(false)} />
    </Box>
  )
}
